<template>
  <StepLayout
    :on-save="onSave"
    :on-next="onNext"
    :on-back="onBack"
    :show-navigation="!isSent"
    step-navigation-button-data-ec="bulk_send_summary_page"
  >
    <template v-slot:title>
      <template v-if="isSent">
        {{ $t('bulkSend.steps.summary.sentTitle') }}
      </template>
      <template v-else>
        {{ $t('bulkSend.steps.summary.title') }}
      </template>
    </template>

    <div class="space-y-base text-black">
      <SummaryItem :title="$t('bulkSend.steps.summary.name.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditName')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_name_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ bulkSend.name }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.sharing.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditSharing')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_sharing_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ $t(`bulkSend.steps.summary.sharing.${sharingType}`, {companyName: company.name}) }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.signingSequence.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditSigningSequence')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_signing_sequence_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ $t(`bulkSend.steps.summary.signingSequence.${signingSequence}`) }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.reminders.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditReminders')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_reminders_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ reminderInterval }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.expires.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditExpiry')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_expiry_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ expiresAt }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem
        :title="$tc('bulkSend.steps.summary.templates.title', bulkSend.templates.length)"
        :to="goTo('BulkSendsStepTemplates', { isEditStep: true })"
      >
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsStepTemplates', { isEditStep: true })"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_templates_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          {{ templateNames }}
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.envelopes.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsStepImportData', { isEditStep: true })"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_import_data_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          <i18n path="bulkSend.steps.summary.envelopes.description" tag="p" class="mb-twenty">
            <template v-slot:link>
              <RouterLink
                :to="goTo('BulkSendsStepReviewData')"
                data-ec="bulk_send_summary_page"
                data-et="go_to_step_review_data_link"
              >{{ $tc('bulkSend.steps.summary.envelopes.link', bulkSend.imported_rows_count, {count: bulkSend.imported_rows_count}) }}</RouterLink>
            </template>
          </i18n>
        </template>
      </SummaryItem>
      <hr/>
      <SummaryItem :title="$t('bulkSend.steps.summary.message.title')">
        <template v-slot:controls>
          <ButtonLink
            :disabled="isSent"
            :to="goTo('BulkSendsEditMessage')"
            size="small"
            variant="secondary"
            data-ec="bulk_send_summary_page"
            data-et="go_to_step_message_button"
          >
            {{ $t('functional.edit') }}
          </ButtonLink>
        </template>
        <template v-slot:content>
          <div class="flex flex-wrap">
            <div
              class="md:line-clamp-none w-full"
              :class="{'line-clamp-3': ! viewAllMessage}"
            >
              {{ message }}
            </div>
            <BaseButton
              v-if="bulkSend.message"
              class="mt-5 md:hidden"
              variant="secondary"
              size="small"
              @click.native="viewAllMessage = !viewAllMessage"
            >
              {{ messageShowHideButtonText }}
            </BaseButton>
          </div>
        </template>
      </SummaryItem>
      <hr/>
      <div
        class="mt-lg border-solid border-1 px-lg py-twenty rounded-10 border-border shadow-component md:flex md:justify-between md:items-center">
        <div class="mr-lg mb-base md:mb-0">
          <p class="font-bold text-16 mb-xs text-black">{{ $t('bulkSend.steps.summary.preview.title') }}</p>
          <p class="text-16 text-black">{{ $t('bulkSend.steps.summary.preview.description') }}</p>
        </div>

        <IconButton
          :disabled="!firstContact"
          :loading="isLoadingPreview"
          class="whitespace-nowrap"
          @click.native="previewBulkSend"
          icon="external-link-alt"
          variant="secondary"
          data-ec="bulk_send_summary_page"
          data-et="launch_preview_button">
          <span>{{ $t('functional.launchPreview') }}</span>
        </IconButton>
      </div>
    </div>
  </StepLayout>
</template>

<script>
import { longDate } from '@/utils/dates';

import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import IconButton from '@/components/library/buttons/IconButton.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import SummaryItem from '@/components/ui/bulkSend/SummaryItem.vue';
import { mapActions, mapGetters } from 'vuex';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import useBulkSendRoutes from '@/composables/bulkSend/routes';
import useList from '@/composables/list/list';
import Meta from '@/store/modules/models/global/Meta';
import { useGetters } from 'vuex-composition-helpers/dist';
import { ImportedRow } from '@/store/modules/bulkSend/models';

export default {
  name: 'SummaryPage',
  components: {
    BaseButton,
    ButtonLink,
    SummaryItem,
    StepLayout,
    IconButton,
  },
  setup() {
    const { prevRoute, nextRoute } = useBulkSendRoutes();
    /** @type {import('@/store/modules/bulkSend').Getters} */
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });

    const [
      fetchImportedRows,
      importedRows,
    ] = useList(
      `bulk-sends/${bulkSend.value.fingerprint}/imported-rows`,
      ImportedRow,
      Meta,
      { per_page: 1 },
    );

    return {
      bulkSend,
      prevRoute,
      nextRoute,
      fetchImportedRows,
      importedRows,
    };
  },
  activated() {
    this.fetchImportedRows();
  },
  data() {
    return {
      viewAllMessage: false,
      isLoadingPreview: false,
    };
  },
  computed: {
    ...mapGetters({
      company: 'company/company',
      authenticatedUser: 'user/user',
      isSent: 'bulkSend/isSent',
    }),
    messageShowHideButtonText() {
      return !this.viewAllMessage
        ? this.$t('bulkSend.steps.summary.message.viewAll')
        : this.$t('bulkSend.steps.summary.message.hide');
    },
    signingSequence() {
      return this.bulkSend.parallel_signing
        ? 'parallel'
        : 'sequential';
    },
    sharingType() {
      if (this.bulkSend.sharing.everyone_permission) {
        return 'everyone';
      }

      if (
        this.bulkSend.sharing.groups.length === 0 &&
        this.bulkSend.sharing.users.length === 1 &&
        this.bulkSend.sharing.users[0].user.id === this.authenticatedUser.id
      ) {
        return 'private';
      }

      return 'individuals';
    },
    expiresAt() {
      if (!this.bulkSend.envelope_expires_after) {
        return this.$t('bulkSend.steps.summary.expires.noneSet');
      }
      return longDate(this.bulkSend.envelope_expires_after);
    },
    reminderInterval() {
      const interval = this.bulkSend.envelope_reminder_interval
        ? this.bulkSend.envelope_reminder_interval / 24
        : 0;

      return this.$tc('bulkSend.steps.summary.reminders.description', interval, {
        interval,
      });
    },
    templateNames() {
      return this.bulkSend.templates.map(template => template.title).join(', ');
    },
    message() {
      return this.bulkSend.message
        ? this.bulkSend.message
        : this.$t('bulkSend.steps.summary.message.noneSet');
    },
    firstContact() {
      return this.importedRows[0]?.imported_contacts[0];
    },
  },
  methods: {
    ...mapActions({
      fetchPreview: 'bulkSend/fetchPreview',
    }),
    goTo(page, props) {
      return {
        name: page,
        params: {
          fingerprint: this.bulkSend.fingerprint,
          ...props,
        },
      };
    },
    async onBack() {
      await this.$router.push(this.prevRoute);
    },
    async onNext() {
      await this.$router.push(this.nextRoute);
    },
    async onSave() {
      await this.$router.push({ name: 'BulkSends' });
    },
    async previewBulkSend() {
      try {
        this.isLoadingPreview = true;
        const preview = await this.fetchPreview(this.firstContact.fingerprint);
        window.open(preview.preview_url);
      } catch (e) {
        // notify?
      } finally {
        this.isLoadingPreview = false;
      }
    },
  },
};
</script>
